import { ChevronDown } from 'lucide-react';
import * as React from 'react';
import { ReactNode } from 'react';

import { Button, ButtonProps, buttonVariants } from 'src/shared/components/ui/buttons/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/shared/components/ui/dropdown-menu';
import { cn } from 'src/shared/lib/utils';

interface ActionItem {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

interface ButtonWithActionsProps extends Omit<ButtonProps, 'asChild' | 'onClick'> {
  children: ReactNode;
  onMainAction: () => void;
  actions: ActionItem[];
  maxWidth?: string;
  dropdownTriggerClassName?: string;
}

const ButtonWithActions: React.FC<ButtonWithActionsProps> = ({
  className,
  variant = 'default',
  children,
  onMainAction,
  actions,
  maxWidth = '220px',
  disabled,
  dropdownTriggerClassName,
  ...props
}: ButtonWithActionsProps) => {
  return (
    <div className="flex relative" style={{ maxWidth }}>
      {/* Main Action Button */}
      <Button
        variant={variant}
        disabled={disabled}
        onClick={onMainAction}
        className={cn('rounded-r-none flex-grow overflow-hidden', className)}
        {...props}
      >
        {children}
      </Button>

      {/* Dropdown Actions */}
      <DropdownMenu>
        <DropdownMenuTrigger
          disabled={disabled}
          className={cn(
            buttonVariants({ variant }),
            'px-2 rounded-l-none border-l border-l-white/20 flex-shrink-0',
            dropdownTriggerClassName
          )}
        >
          <ChevronDown size={16} />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={5}>
          {actions.map((action, index) => (
            <DropdownMenuItem
              key={index}
              onClick={action.onClick}
              disabled={action.disabled}
              className="cursor-pointer"
            >
              {action.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

ButtonWithActions.displayName = 'ButtonWithActions';

export { ButtonWithActions };
export type { ActionItem, ButtonWithActionsProps };
